import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useCallback, useMemo, useState } from "react";
import { TextInput, type RaRecord } from "react-admin";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useFormContext } from "react-hook-form";
import {
  NO_EXTRA_ITS_FILTER,
  itsAutoDefaultHelperText,
  useItsAutoLookup,
} from "@/components/its-lookup";
import { itsGenderToSadaratGender } from "@/utils/sadarat-gender";

const defaultHelperText = `${itsAutoDefaultHelperText()} (optional — leave empty to enter name/mobile only)`;

type LookupHint =
  | { variant: "default" }
  | { variant: "notInDirectory"; its: string }
  | { variant: "lookupError"; message: string };

function setDefaultHintIfNeeded(setter: Dispatch<SetStateAction<LookupHint>>) {
  setter((prev) => (prev.variant === "default" ? prev : { variant: "default" }));
}

function hintToHelperNode(hint: LookupHint): ReactNode {
  if (hint.variant === "default") {
    return defaultHelperText;
  }
  if (hint.variant === "notInDirectory") {
    return (
      <Box component="span" sx={{ typography: "caption", color: "text.secondary" }}>
        ITS {hint.its} is not in the directory — enter name/mobile manually below
      </Box>
    );
  }
  return (
    <Box component="span" sx={{ typography: "caption", color: "error.main" }}>
      {hint.message}
    </Box>
  );
}

/**
 * Optional ITS on sadarat master: type 8 digits to auto-fill from itsdata,
 * or leave empty / unknown ITS and enter name/mobile manually.
 */
export default function SadaratItsPickerInput() {
  const { setValue } = useFormContext();
  const [lookupHint, setLookupHint] = useState<LookupHint>({ variant: "default" });

  const onFound = useCallback(
    (row: RaRecord) => {
      setValue("itsNo", String(row.ITS_ID ?? "").trim());
      setValue("name", String(row.Full_Name ?? "").trim());
      setValue("mobile", String(row.Mobile ?? "").trim());
      const mapped = itsGenderToSadaratGender(row.Gender);
      if (mapped) {
        setValue("gender", mapped, { shouldDirty: true, shouldValidate: true });
      }
      setDefaultHintIfNeeded(setLookupHint);
    },
    [setValue]
  );

  const onNotFound = useCallback((its: string) => {
    setLookupHint({ variant: "notInDirectory", its });
  }, []);

  const onError = useCallback((e: unknown) => {
    console.error(e);
    setLookupHint({
      variant: "lookupError",
      message: e instanceof Error ? e.message : "ITS lookup failed",
    });
  }, []);

  const onIncomplete = useCallback(() => {
    setDefaultHintIfNeeded(setLookupHint);
  }, []);

  const { loading } = useItsAutoLookup({
    source: "itsNo",
    filter: NO_EXTRA_ITS_FILTER,
    onFound,
    onNotFound,
    onError,
    onIncomplete,
  });

  const helperText = useMemo(() => hintToHelperNode(lookupHint), [lookupHint]);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextInput source="itsNo" label="ITS (optional)" fullWidth helperText={helperText} />
      </Box>
      {loading ? <CircularProgress size={22} sx={{ mt: 2 }} /> : null}
    </Box>
  );
}
